/**
 * Building External Contacts API
 *
 * Links external users (users/userList, isExternal: true) to a building via
 * buildings/{buildingKey}/externalContacts/{externalUserId}. Mirrors the
 * project external-contact methods in projectsApi.ts.
 *
 * Display reads users/userList directly (not the admin-gated externalUserApi
 * 'list' callable) so the read-only contact list renders for non-admins too.
 */
import { getDatabase, ref, get, set, update, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { ExternalUser } from '../types/guestAccess';
import { BuildingExternalContact } from '../types/buildings';
import { recordActivity } from './activityApi';

/** Max length for the free-text building note written directly to the DB. */
export const MAX_BUILDING_NOTE_LENGTH = 500;

/** A building link joined with the external user it references. */
export interface BuildingExternalContactWithUser {
    externalUserId: string;
    addedBy: string;
    addedAt: string;
    buildingNote?: string;
    user: ExternalUser;
}

const db = getDatabase();

function getCurrentUser() {
    const user = getAuth().currentUser;
    if (!user) {
        throw new Error('Du måste vara inloggad');
    }
    return user;
}

function contactsPath(buildingKey: string): string {
    return `buildings/${buildingKey}/externalContacts`;
}

function contactPath(buildingKey: string, externalUserId: string): string {
    return `${contactsPath(buildingKey)}/${externalUserId}`;
}

/** Parse a single external user record; returns null if not a valid external user. */
function parseExternalUser(data: unknown, uid: string): ExternalUser | null {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (d.isExternal !== true) return null;
    if (typeof d.name !== 'string' || typeof d.email !== 'string') return null;
    if (typeof d.createdAt !== 'string' || typeof d.createdBy !== 'string') return null;
    return {
        uid,
        name: d.name,
        email: d.email,
        phone: typeof d.phone === 'string' ? d.phone : undefined,
        company: typeof d.company === 'string' ? d.company : undefined,
        role: typeof d.role === 'string' ? d.role : undefined,
        isExternal: true,
        createdAt: d.createdAt,
        createdBy: d.createdBy,
        updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : undefined,
    };
}

export const buildingExternalContactsApi = {
    /**
     * Read the external-user registry directly from users/userList.
     * Filtered to isExternal === true, sorted by name (Swedish locale).
     * Open to any authenticated user (unlike the admin-gated 'list' callable),
     * so the building contact list renders for non-admins.
     */
    async listRegistry(): Promise<ExternalUser[]> {
        const snapshot = await get(ref(db, 'users/userList'));
        if (!snapshot.exists()) return [];
        const usersDict = snapshot.val() as Record<string, unknown>;
        const users: ExternalUser[] = [];
        for (const [uid, data] of Object.entries(usersDict)) {
            const user = parseExternalUser(data, uid);
            if (user) users.push(user);
        }
        return users.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    },

    /**
     * List the external contacts linked to a building, joined with their
     * registry records. Links whose user no longer exists are filtered out
     * (read-time dangling-link handling). Sorted by name.
     */
    async listForBuilding(buildingKey: string): Promise<BuildingExternalContactWithUser[]> {
        if (!buildingKey) throw new Error('buildingKey krävs');

        const [linksSnapshot, registry] = await Promise.all([
            get(ref(db, contactsPath(buildingKey))),
            this.listRegistry(),
        ]);

        if (!linksSnapshot.exists()) return [];
        const links = linksSnapshot.val() as Record<string, BuildingExternalContact>;
        const usersById = new Map(registry.map((u) => [u.uid, u]));

        const result: BuildingExternalContactWithUser[] = [];
        for (const [externalUserId, link] of Object.entries(links)) {
            const user = usersById.get(externalUserId);
            if (!user) continue; // dangling link — registry user deleted
            result.push({
                externalUserId,
                addedBy: link.addedBy,
                addedAt: link.addedAt,
                buildingNote: link.buildingNote,
                user,
            });
        }
        return result.sort((a, b) => a.user.name.localeCompare(b.user.name, 'sv'));
    },

    /**
     * Link an external contact to a building.
     */
    async addBuildingExternalContact(
        buildingKey: string,
        externalUserId: string,
        buildingNote?: string
    ): Promise<void> {
        if (!buildingKey) throw new Error('buildingKey krävs');
        if (!externalUserId) throw new Error('Extern användare krävs');

        const user = getCurrentUser();
        const path = contactPath(buildingKey, externalUserId);

        const existingSnapshot = await get(ref(db, path));
        if (existingSnapshot.exists()) {
            throw new Error('Denna kontakt finns redan i byggnaden');
        }

        const note = buildingNote?.trim().slice(0, MAX_BUILDING_NOTE_LENGTH);
        const contactData = {
            externalUserId,
            addedBy: user.uid,
            addedAt: formatDateForStorage(new Date()),
            ...(note && { buildingNote: note }),
        };

        await set(ref(db, path), contactData);
        recordActivity('building.updated', { buildingKey, targetKey: buildingKey });
    },

    /**
     * Remove an external contact from a building.
     */
    async removeBuildingExternalContact(
        buildingKey: string,
        externalUserId: string
    ): Promise<void> {
        await remove(ref(db, contactPath(buildingKey, externalUserId)));
        recordActivity('building.updated', { buildingKey, targetKey: buildingKey });
    },

    /**
     * Update (or clear, when empty) the building note for an external contact.
     */
    async updateBuildingExternalContactNote(
        buildingKey: string,
        externalUserId: string,
        note: string
    ): Promise<void> {
        const path = contactPath(buildingKey, externalUserId);
        const snapshot = await get(ref(db, path));
        if (!snapshot.exists()) {
            throw new Error('Kontakten hittades inte');
        }

        const trimmed = note.trim().slice(0, MAX_BUILDING_NOTE_LENGTH);
        if (trimmed) {
            await update(ref(db, path), { buildingNote: trimmed });
        } else {
            const updates: Record<string, string | null> = {};
            updates[`${path}/buildingNote`] = null;
            await update(ref(db), updates);
        }
        recordActivity('building.updated', { buildingKey, targetKey: buildingKey });
    },
};
